/**
 * 포털 공통 셸 — Claude 문서 스타일 개편(2026-07-04, 사용자 결정).
 *   상단: 로고 | 문서▾ | 도구▾ | 자료실▾ | CTA — 평면 9탭 나열 제거, API 키는 상단에서
 *   빼고 문서 트리(DocsNav)로 편입. 문서 페이지는 3단: 좌=문서 트리, 중=본문, 우=페이지 목차(toc).
 *   도구·자료실 페이지는 사이드바 없이 전체 폭(성격이 앱/목록이라 문서 레이아웃 강요 안 함).
 */
import React, { useEffect, useState } from "react";

const C = {
  bar: {
    position: "sticky", top: 0, zIndex: 20,
    background: "rgba(255,255,255,0.85)", backdropFilter: "blur(8px)",
    borderBottom: "1px solid #e5e7eb",
  },
  barIn: {
    maxWidth: 1180, margin: "0 auto", padding: "0 20px", height: 56,
    display: "flex", alignItems: "center", gap: 6,
  },
  logo: {
    display: "flex", alignItems: "center", gap: 8, cursor: "pointer",
    fontWeight: 800, fontSize: 16, color: "#111827", whiteSpace: "nowrap", marginRight: 12,
  },
  logoBadge: {
    width: 26, height: 26, borderRadius: 7, background: "#111827", color: "#fff",
    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800,
  },
  group: { position: "relative" },
  groupBtn: (on) => ({
    fontSize: 14, fontWeight: 600, cursor: "pointer", padding: "6px 12px", borderRadius: 7,
    color: on ? "#111827" : "#6b7280", background: on ? "#f3f4f6" : "transparent",
    whiteSpace: "nowrap", border: "none", display: "flex", alignItems: "center", gap: 4,
  }),
  menu: {
    position: "absolute", top: "calc(100% + 6px)", left: 0, minWidth: 180,
    background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10,
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)", padding: 6, zIndex: 30,
  },
  menuItem: (on) => ({
    display: "block", width: "100%", textAlign: "left", border: "none", cursor: "pointer",
    padding: "8px 10px", borderRadius: 7, fontSize: 13.5,
    fontWeight: on ? 700 : 500, color: on ? "#111827" : "#4b5563",
    background: on ? "#f3f4f6" : "transparent",
  }),
  right: { marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 },
  cta: {
    background: "#111827", color: "#fff", border: "none", borderRadius: 8,
    padding: "8px 14px", fontSize: 13.5, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
  },
  wrap: {
    maxWidth: 1180, margin: "0 auto", padding: "28px 20px 60px",
    display: "flex", gap: 32, alignItems: "flex-start", width: "100%", boxSizing: "border-box",
  },
  side: { position: "sticky", top: 76, width: 190, flexShrink: 0, fontSize: 13.5 },
  toc: { position: "sticky", top: 76, width: 170, flexShrink: 0, fontSize: 12.5 },
  tocHead: {
    fontSize: 11.5, fontWeight: 700, color: "#9ca3af", letterSpacing: 0.5,
    textTransform: "uppercase", margin: "0 0 6px 10px",
  },
  sideItem: {
    display: "block", padding: "6px 10px", borderRadius: 6, color: "#6b7280",
    textDecoration: "none", cursor: "pointer",
  },
  navGroupHead: {
    fontSize: 13, fontWeight: 800, color: "#111827",
    margin: "16px 0 4px 10px",
  },
  navItem: (on) => ({
    display: "block", padding: "6px 10px", borderRadius: 6, cursor: "pointer",
    color: on ? "#111827" : "#6b7280", fontWeight: on ? 700 : 500,
    background: on ? "#f3f4f6" : "transparent",
  }),
  content: { flex: 1, minWidth: 0 },
  embeddedWrap: {
    maxWidth: 1180, margin: "0 auto", padding: "28px 24px 60px",
    width: "100%", boxSizing: "border-box", color: "#1f2937",
    fontFamily: "'Inter','Pretendard',sans-serif",
  },
  foot: { borderTop: "1px solid #e5e7eb", marginTop: 40 },
  footIn: {
    maxWidth: 1180, margin: "0 auto", padding: "18px 20px", fontSize: 12.5, color: "#9ca3af",
    display: "flex", flexWrap: "wrap", gap: "6px 14px", alignItems: "center",
  },
  footLink: { color: "#6b7280", textDecoration: "none", cursor: "pointer" },
};

// 상단 그룹 — 꼭 필요한 것만. API 키는 문서 트리로 편입(상단 제거).
const NAV_GROUPS = [
  { key: "docs", label: "가이드", items: [
    { page: "guide", active: "guide", label: "사용자 가이드" },
    { page: "api-docs", active: "docs", label: "API 문서" },
  ]},
  { key: "tools", label: "무료 도구", items: [
    { page: "freight", active: "freight", label: "특송 운임계산" },
    { page: "taxcalc", active: "taxcalc", label: "세금계산" },
    { page: "exchange", active: "exchange", label: "관세환율" },
    { page: "trade-codes", active: "trade-codes", label: "무역통계부호" },
    { page: "req-agencies", active: "req-agencies", label: "요건확인기관 연락처" },
    { page: "forms", active: "forms", label: "서식자료" },
  ]},
  { key: "archive", label: "자료실", items: [
    { page: "cases", active: "cases", label: "분류 사례" },
  ]},
];
// API 키는 문서 그룹 소속으로 취급(그룹 하이라이트용)
const GROUP_OF = { guide: "docs", docs: "docs", keys: "docs",
  freight: "tools", taxcalc: "tools", exchange: "tools", "trade-codes": "tools",
  "req-agencies": "tools",
  forms: "tools", cases: "archive", news: "news" };

export default function ApiPortalLayout({ active, onNavigate, sidebar, toc, children, embedded = false }) {
  const loggedIn = !!(localStorage.getItem("token") || sessionStorage.getItem("token"));
  const [open, setOpen] = useState(null); // 열린 드롭다운 key
  const [wide, setWide] = useState(
    typeof window !== "undefined" ? window.innerWidth > 1080 : true,
  );
  useEffect(() => {
    const f = () => setWide(window.innerWidth > 1080);
    window.addEventListener("resize", f);
    return () => window.removeEventListener("resize", f);
  }, []);

  if (embedded) {
    return (
      <div style={C.embeddedWrap} data-testid="embedded-public-content">
        <main style={C.content}>{children}</main>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh", background: "#fff", color: "#1f2937",
        fontFamily: "'Inter','Pretendard',sans-serif", display: "flex", flexDirection: "column",
      }}
      onClick={() => setOpen(null)}
    >
      <header style={C.bar}>
        <div style={C.barIn}>
          <div style={C.logo} onClick={() => onNavigate("landing")}>
            <span style={C.logoBadge}>
              <svg width="17" height="17" viewBox="0 0 48 48" fill="none">
                <g stroke="#fff" strokeWidth="5" strokeLinecap="round">
                  <line x1="14" y1="12" x2="14" y2="36" />
                  <line x1="34" y1="12" x2="34" y2="36" />
                </g>
                <polyline points="17,24 23,29 31,18" stroke="#4285F4" strokeWidth="4.8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
              </svg>
            </span>
            LoginHS
          </div>
          {NAV_GROUPS.map((g) => {
            const on = GROUP_OF[active] === g.key;
            return (
              <div
                key={g.key}
                style={C.group}
                onMouseEnter={() => setOpen(g.key)}
                onMouseLeave={() => setOpen((o) => (o === g.key ? null : o))}
              >
                <button
                  style={C.groupBtn(on)}
                  onClick={(e) => { e.stopPropagation(); setOpen(open === g.key ? null : g.key); }}
                >
                  {g.label} <span style={{ fontSize: 10, color: "#9ca3af" }}>▾</span>
                </button>
                {open === g.key && (
                  <div style={C.menu}>
                    {g.items.map((it) => (
                      <button
                        key={it.page}
                        style={C.menuItem(active === it.active)}
                        onClick={() => { setOpen(null); onNavigate(it.page); }}
                      >
                        {it.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          <button
            style={C.groupBtn(active === "news")}
            onClick={() => onNavigate("news")}
          >
            새소식
          </button>
          <div style={C.right}>
            {loggedIn ? (
              <button style={C.cta} onClick={() => { window.location.href = "/"; }}>
                앱으로 이동 →
              </button>
            ) : (
              <button style={C.cta} onClick={() => onNavigate("login")}>로그인 / 시작하기</button>
            )}
          </div>
        </div>
      </header>

      <div style={{ ...C.wrap, flex: 1 }}>
        {wide && <nav style={C.side}><PortalNav active={active} onNavigate={onNavigate} /></nav>}
        <main style={C.content}>{children}</main>
        {toc && wide && (
          <nav style={C.toc}>
            <div style={C.tocHead}>이 페이지</div>
            {toc}
          </nav>
        )}
      </div>

      <footer style={C.foot}>
        <div style={C.footIn}>
          <span>© (주)로그인네트웍 · loginHS 세번분류 API</span>
          <a style={C.footLink} href="/openapi/loginhs-classify-v1.json" download>
            OpenAPI 다운로드
          </a>
          <span style={C.footLink} onClick={() => onNavigate("contact")}>문의하기</span>
        </div>
      </footer>
    </div>
  );
}

/** 문서 목차 항목(우측 '이 페이지' 레일용) — 앵커로 부드럽게 스크롤. */
export function SideTocItem({ href, children }) {
  return (
    <a
      style={C.sideItem}
      href={href}
      onClick={(e) => {
        e.preventDefault();
        const el = document.querySelector(href);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }}
    >
      {children}
    </a>
  );
}

/** 좌측 사이트 메뉴 트리 — 모든 포털 페이지 공통(그룹 볼드 + 하위 메뉴).
 *  우측 레일이 "현재 메뉴의 상세 목록/목차"를 담당(가이드=문서 목록, 문서=앵커 목차). */
const PORTAL_TREE = [
  { head: "가이드", items: [
    { key: "guide", label: "사용자 가이드", go: "guide" },
    { key: "docs", label: "API 문서", go: "api-docs" },
    { key: "keys", label: "인증 · 키 발급", go: "api-keys" },
  ]},
  { head: "무료 도구", items: [
    { key: "freight", label: "특송 운임계산", go: "freight" },
    { key: "taxcalc", label: "세금계산", go: "taxcalc" },
    { key: "exchange", label: "관세환율", go: "exchange" },
    { key: "trade-codes", label: "무역통계부호", go: "trade-codes" },
    { key: "req-agencies", label: "요건확인기관 연락처", go: "req-agencies" },
    { key: "forms", label: "서식자료", go: "forms" },
  ]},
  { head: "자료실", items: [
    { key: "cases", label: "분류 사례", go: "cases" },
  ]},
  { head: "새소식", items: [
    { key: "news", label: "새소식 (대시보드)", go: "news" },
  ]},
];

export function PortalNav({ active, onNavigate }) {
  return (
    <div>
      {PORTAL_TREE.map((g) => (
        <div key={g.head}>
          <div style={C.navGroupHead}>{g.head}</div>
          {g.items.map((it) => (
            <span
              key={it.key}
              style={C.navItem(active === it.key)}
              onClick={() => onNavigate(it.go)}
            >
              {it.label}
            </span>
          ))}
        </div>
      ))}
    </div>
  );
}
